import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { IConfig } from '@onlyoffice/document-editor-angular';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { PdfViewerComponent } from 'src/app/@shared/components/pdf-viewer/pdf-viewer.component';
import { OnlyOfficeDocEditorComponent } from 'src/app/@shared/components/only-office-doc-editor/only-office-doc-editor.component';
import { ToastService } from 'src/app/@core/services/toast.service';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { JWTService } from 'src/app/contract-management/services/jwt.service';
import { ONLY_OFFICE_EDITOR_TYPE } from 'src/app/@core/models/enums';
import { environment } from 'src/environments/environment.dev';
import {
    GeotechnicalAnalysisService,
    GEOTECHNICAL_AI_DASHBOARD_STATUS,
    GEOTECHNICAL_SUMMARY_STATUS,
} from 'src/app/features/geotechnical-analysis/services/geotechnical-analysis.service';
import { GeoTechnicalAnalysisReportDataService } from 'src/app/features/geotechnical-analysis/services/geo-technical-analysis-report-data.service';

type DownloadMode = 'preview' | 'download' | 'open';

interface ExportItem {
    icon: string;
    title: string;
    description: string;
    formats: string;
    /** Triggers the export action when provided; null = not yet implemented */
    action: ((mode: DownloadMode) => void) | null;
    /**
     * Subset of modes this item supports.
     * When omitted all three (preview / download / open) are shown.
     * XLSX exports only support 'download' since Excel can't be rendered in-browser.
     */
    supportedModes?: DownloadMode[];
}

/**
 * Exports tab for the geotechnical analysis report.
 * Report Summary and Analysis Summary are fully wired;
 * AI Dashboard (HTML) is wired with preview/download/open.
 * Remaining XLSX items are stubs (coming soon).
 */
@Component({
    selector: 'app-geo-technical-exports-tab',
    standalone: true,
    imports: [CommonModule, ButtonComponent, DialogComponent, PdfViewerComponent, OnlyOfficeDocEditorComponent],
    templateUrl: './exports-tab.component.html',
    styles: [`:host { display: block; height: 100%; width: 100%; overflow-y: auto; }`],
})
export class GeoTechnicalExportsTabComponent {
    private dataService = inject(GeoTechnicalAnalysisReportDataService);
    private geoService = inject(GeotechnicalAnalysisService);
    private toast = inject(ToastService);
    private sanitizer = inject(DomSanitizer);
    private authService = inject(AuthenticationService);
    private jwtService = new JWTService();

    /** OnlyOffice config used for XLSX preview dialog */
    xlsxEditorConfig: IConfig = {};
    xlsxPreviewVisible = false;
    xlsxPreviewTitle = '';

    /** Loading state: key is `'itemIndex:mode'`, value is true while in-flight */
    loadingMap = signal<Record<string, boolean>>({});

    /** Controls which content type is shown inside the shared preview dialog */
    previewType: 'pdf' | 'html' = 'pdf';

    /** PDF preview dialog state */
    previewVisible = false;
    previewUrl = '';
    previewTitle = '';

    /** HTML preview: sanitised blob URL used as the iframe src */
    previewHtmlSrc: SafeResourceUrl | null = null;
    /** Raw blob URL — kept separately so it can be revoked on dialog close */
    private _htmlBlobUrl: string | null = null;

    readonly exportItems: ExportItem[] = [
        {
            icon: 'pi pi-chart-line',
            title: 'Report Summary',
            description: 'High-level overview of the geotechnical analysis findings',
            formats: 'PDF',
            action: (mode) => this.handleReportSummary(mode),
        },
        {
            icon: 'pi pi-file-edit',
            title: 'Analysis Summary',
            description: 'AI-generated narrative summary of the full analysis',
            formats: 'PDF',
            action: (mode) => this.handleAnalysisSummary(mode),
        },
        {
            icon: 'pi pi-objects-column',
            title: 'AI Dashboard',
            description: 'Interactive AI Dashboard export with charts and insights',
            formats: 'HTML',
            action: (mode) => this.handleAiDashboard(mode),
        },
        {
            icon: 'pi pi-list-check',
            title: 'Alternative Analysis Results',
            description: 'Ranked alternates with cost, schedule, and impact breakdown',
            formats: 'XLSX',
            action: (mode) => this.handleAlternativeAnalysisResults(mode),
            supportedModes: ['preview', 'download'],
        },
        {
            icon: 'pi pi-dollar',
            title: 'Resource Cost Breakdown',
            description: 'Material, equipment, and labour costs per work item across all alternates',
            formats: 'XLSX',
            action: (mode) => this.handleResourceCostBreakdown(mode),
            supportedModes: ['preview', 'download'],
        },
        {
            icon: 'pi pi-briefcase',
            title: 'Work Cost Breakdown',
            description: 'General works and stabilisation works cost per alternate',
            formats: 'XLSX',
            action: (mode) => this.handleWorkCostBreakdown(mode),
            supportedModes: ['preview', 'download'],
        },
        {
            icon: 'pi pi-chart-bar',
            title: 'Impact vs. Alternate',
            description: 'Schedule impact and cost trade-off across all alternates',
            formats: 'XLSX',
            action: (mode) => this.handleImpactVsAlternate(mode),
            supportedModes: ['preview', 'download'],
        },
    ];

    isLoading(index: number, mode: DownloadMode): boolean {
        return !!this.loadingMap()[`${index}:${mode}`];
    }

    onAction(item: ExportItem, index: number, mode: DownloadMode): void {
        if (!item.action) return;
        // For items that only support certain modes, route unsupported clicks to download
        const effectiveMode = item.supportedModes && !item.supportedModes.includes(mode)
            ? 'download' as DownloadMode
            : mode;
        this.setLoading(index, effectiveMode, true);
        item.action(effectiveMode);
    }

    /** Returns true when the given mode button should be rendered for an item. */
    supportsMode(item: ExportItem, mode: DownloadMode): boolean {
        return !item.supportedModes || item.supportedModes.includes(mode);
    }

    private setLoading(index: number, mode: DownloadMode, value: boolean): void {
        this.loadingMap.update(m => ({ ...m, [`${index}:${mode}`]: value }));
    }

    // ── Report Summary (index 0) ─────────────────────────────────────────

    private handleReportSummary(mode: DownloadMode): void {
        const analysisId = this.dataService.analysisReportId();
        if (!analysisId) {
            this.toast.error('Analysis ID not found');
            this.clearAllLoading(0);
            return;
        }

        // Step 1: fetch the latest summary to get its document _id and current status
        this.geoService.fetchLatestSummary(analysisId).subscribe({
            next: (res) => {
                const status = res.data?.summaryStatus;
                const summaryId = res.data?._id;
                // Guard: only proceed if the latest summary is fully COMPLETED;
                // a previous _id may still exist while regeneration is in progress.
                if (!res.success || !summaryId || status !== GEOTECHNICAL_SUMMARY_STATUS.COMPLETED) {
                    this.showSummaryStatusToast(status, 'Report Summary');
                    this.clearAllLoading(0);
                    return;
                }

                // Step 2: download the PDF using the summary document _id
                this.geoService.downloadSummaryById(summaryId).subscribe({
                    next: async (response) => {
                        const blob = response.body;
                        if (!blob) {
                            this.toast.error('Report Summary not available');
                            this.clearAllLoading(0);
                            return;
                        }

                        // Guard: if server returned a JSON error instead of a PDF blob
                        const contentType = response.headers.get('Content-Type') ?? blob.type ?? '';
                        if (contentType.includes('application/json')) {
                            try {
                                const json = JSON.parse(await blob.text());
                                this.toast.error(json?.message ?? 'Report Summary not available');
                            } catch {
                                this.toast.error('Report Summary not available');
                            }
                            this.clearAllLoading(0);
                            return;
                        }

                        const filename = this.extractFilename(
                            response.headers.get('Content-Disposition'),
                            'report-summary.pdf',
                        );
                        this.handleBlob(blob, filename, mode, 'Report Summary');
                        this.clearAllLoading(0);
                    },
                    error: () => {
                        this.toast.error('Failed to download Report Summary. Please try again.');
                        this.clearAllLoading(0);
                    },
                });
            },
            error: () => {
                this.toast.error('Failed to fetch Report Summary. Please try again.');
                this.clearAllLoading(0);
            },
        });
    }

    // ── Analysis Summary (index 1) ──────────────────────────────────────

    private handleAnalysisSummary(mode: DownloadMode): void {
        const analysisId = this.dataService.analysisReportId();
        if (!analysisId) {
            this.toast.error('Analysis ID not found');
            this.clearAllLoading(1);
            return;
        }

        // Step 1: fetch the latest analysis summary to get its document _id and current status
        this.geoService.fetchLatestAnalysisSummary(analysisId).subscribe({
            next: (res) => {
                // Analysis summary status lives in analysisSummaryStatus field
                const status = res.data?.analysisSummaryStatus;
                const summaryId = res.data?._id;
                // Guard: only proceed if the latest summary is fully COMPLETED;
                // a previous _id may still exist while regeneration is in progress.
                if (!res.success || !summaryId || status !== GEOTECHNICAL_SUMMARY_STATUS.COMPLETED) {
                    this.showSummaryStatusToast(status, 'Analysis Summary');
                    this.clearAllLoading(1);
                    return;
                }

                // Step 2: download the PDF using the summary document _id
                this.geoService.downloadAnalysisSummaryById(summaryId).subscribe({
                    next: async (response) => {
                        const blob = response.body;
                        if (!blob) {
                            this.toast.error('Analysis Summary not available');
                            this.clearAllLoading(1);
                            return;
                        }

                        // Guard: if server returned a JSON error instead of a PDF blob
                        const contentType = response.headers.get('Content-Type') ?? blob.type ?? '';
                        if (contentType.includes('application/json')) {
                            try {
                                const json = JSON.parse(await blob.text());
                                this.toast.error(json?.message ?? 'Analysis Summary not available');
                            } catch {
                                this.toast.error('Analysis Summary not available');
                            }
                            this.clearAllLoading(1);
                            return;
                        }

                        const filename = this.extractFilename(
                            response.headers.get('Content-Disposition'),
                            'analysis-summary.pdf',
                        );
                        this.handleBlob(blob, filename, mode, 'Analysis Summary');
                        this.clearAllLoading(1);
                    },
                    error: () => {
                        this.toast.error('Failed to download Analysis Summary. Please try again.');
                        this.clearAllLoading(1);
                    },
                });
            },
            error: () => {
                this.toast.error('Failed to fetch Analysis Summary. Please try again.');
                this.clearAllLoading(1);
            },
        });
    }

    // ── AI Dashboard (index 2) ───────────────────────────────────────────

    private handleAiDashboard(mode: DownloadMode): void {
        const analysisId = this.dataService.analysisReportId();
        if (!analysisId) {
            this.toast.error('Analysis ID not found');
            this.clearAllLoading(2);
            return;
        }

        // Step 1: fetch the dashboard status + signed URL from the backend
        this.geoService.getAiDashboard(analysisId).subscribe({
            next: async (res: any) => {
                const data = res?.data;
                const status: GEOTECHNICAL_AI_DASHBOARD_STATUS = data?.status;

                if (status !== GEOTECHNICAL_AI_DASHBOARD_STATUS.COMPLETED || !data?.signedUrl) {
                    const msg =
                        status === GEOTECHNICAL_AI_DASHBOARD_STATUS.IN_PROGRESS
                            ? 'AI Dashboard is still being generated. Please wait.'
                            : status === GEOTECHNICAL_AI_DASHBOARD_STATUS.FAILED
                                ? 'AI Dashboard generation failed. Please regenerate from the AI Dashboard tab.'
                                : 'AI Dashboard has not been generated yet. Please generate it from the AI Dashboard tab.';
                    this.toast.info(msg);
                    this.clearAllLoading(2);
                    return;
                }

                // Step 2: fetch the raw HTML from S3 via the signed URL
                try {
                    const html = await fetch(data.signedUrl).then(r => r.text());
                    const analysisName =
                        this.dataService.analysisReport()?.displayName?.trim() || 'Geotechnical Analysis';
                    const safeName = analysisName.replace(/[/\\?%*:|"<>]/g, '-');
                    const filename = `${safeName} AI Dashboard.html`;

                    const blob = new Blob([html], { type: 'text/html' });

                    switch (mode) {
                        case 'preview': {
                            // Revoke any previous HTML blob to avoid memory leaks
                            if (this._htmlBlobUrl) URL.revokeObjectURL(this._htmlBlobUrl);
                            this._htmlBlobUrl = URL.createObjectURL(blob);
                            this.previewHtmlSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this._htmlBlobUrl);
                            this.previewType = 'html';
                            this.previewTitle = 'AI Dashboard';
                            this.previewVisible = true;
                            break;
                        }
                        case 'download': {
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = filename;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            setTimeout(() => URL.revokeObjectURL(url), 1000);
                            this.toast.success('AI Dashboard downloaded successfully');
                            break;
                        }
                        case 'open': {
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                            // Give the browser time to load before revoking
                            setTimeout(() => URL.revokeObjectURL(url), 5000);
                            break;
                        }
                    }
                } catch {
                    this.toast.error('Failed to fetch AI Dashboard. Please try again.');
                }

                this.clearAllLoading(2);
            },
            error: () => {
                this.toast.error('Failed to fetch AI Dashboard. Please try again.');
                this.clearAllLoading(2);
            },
        });
    }

    // ── Alternative Analysis Results (index 3) ──────────────────────────────

    /**
     * Calls the backend to generate the Alternative Analysis Results XLSX.
     * Sheet mirrors the "Alternative analysis results" table in the Value Engineering tab:
     *  - Layer header rows (light blue)
     *  - Parameter rows with values per alternate
     *  - Method rows (italic) showing stabilisation methods applied per alternate
     * - 'preview'  → opens in OnlyOffice (view-only)
     * - 'download' → fetches blob and triggers browser save-as
     */
    private handleAlternativeAnalysisResults(mode: DownloadMode): void {
        const analysisId = this.dataService.analysisReportId();
        if (!analysisId) {
            this.toast.error('Analysis ID not found');
            this.clearAllLoading(3);
            return;
        }

        this.geoService.exportAlternativeAnalysisResults(analysisId).subscribe({
            next: async (res) => {
                const signedUrl = res.data?.signedUrl;
                if (!res.success || !signedUrl) {
                    this.toast.error(res.message ?? 'Failed to generate Alternative Analysis Results');
                    this.clearAllLoading(3);
                    return;
                }

                const analysisName =
                    this.dataService.analysisReport()?.displayName?.trim() || 'Geotechnical Analysis';
                const safeName = analysisName.replace(/[/\\?%*:|"<>]/g, '-');

                if (mode === 'preview') {
                    this.openXlsxInOnlyOffice(signedUrl, `${safeName} - Alternative Analysis Results`);
                } else {
                    try {
                        await this.downloadXlsxBlob(signedUrl, `${safeName} - Alternative Analysis Results.xlsx`);
                        this.toast.success('Alternative Analysis Results downloaded successfully');
                    } catch (err) {
                        const msg = err instanceof Error ? err.message : 'Failed to download file';
                        this.toast.error(msg + '. Please try again.');
                    }
                }

                this.clearAllLoading(3);
            },
            error: () => {
                this.toast.error('Failed to generate Alternative Analysis Results. Please try again.');
                this.clearAllLoading(3);
            },
        });
    }

    // ── Resource Cost Breakdown (index 4) ────────────────────────────────

    /**
     * Calls the backend to generate the Resource Cost Breakdown XLSX, upload it to S3,
     * and return a signed URL.
     * - 'preview'  → opens the XLSX in OnlyOffice (view-only mode)
     * - 'download' → fetches the blob from the signed URL and triggers a browser download
     */
    private handleResourceCostBreakdown(mode: DownloadMode): void {
        const analysisId = this.dataService.analysisReportId();
        if (!analysisId) {
            this.toast.error('Analysis ID not found');
            this.clearAllLoading(4);
            return;
        }

        this.geoService.exportResourceCostBreakdown(analysisId).subscribe({
            next: async (res) => {
                const signedUrl = res.data?.signedUrl;
                if (!res.success || !signedUrl) {
                    this.toast.error(res.message ?? 'Failed to generate Resource Cost Breakdown');
                    this.clearAllLoading(4);
                    return;
                }

                const analysisName =
                    this.dataService.analysisReport()?.displayName?.trim() || 'Geotechnical Analysis';
                const safeName = analysisName.replace(/[/\\?%*:|"<>]/g, '-');

                if (mode === 'preview') {
                    // Open in OnlyOffice view mode using the S3-signed URL
                    this.openXlsxInOnlyOffice(signedUrl, `${safeName} - Resource Cost Breakdown`);
                } else {
                    try {
                        await this.downloadXlsxBlob(signedUrl, `${safeName} - Resource Cost Breakdown.xlsx`);
                        this.toast.success('Resource Cost Breakdown downloaded successfully');
                    } catch (err) {
                        const msg = err instanceof Error ? err.message : 'Failed to download file';
                        this.toast.error(msg + '. Please try again.');
                    }
                }

                this.clearAllLoading(4);
            },
            error: () => {
                this.toast.error('Failed to generate Resource Cost Breakdown. Please try again.');
                this.clearAllLoading(4);
            },
        });
    }

    // ── Work Cost Breakdown (index 5) ────────────────────────────────────

    /**
     * Calls the backend to generate the Work Cost Breakdown XLSX.
     * - 'preview'  → opens in OnlyOffice (view-only)
     * - 'download' → fetches blob and triggers browser save-as
     */
    private handleWorkCostBreakdown(mode: DownloadMode): void {
        const analysisId = this.dataService.analysisReportId();
        if (!analysisId) {
            this.toast.error('Analysis ID not found');
            this.clearAllLoading(5);
            return;
        }

        this.geoService.exportWorkCostBreakdown(analysisId).subscribe({
            next: async (res) => {
                const signedUrl = res.data?.signedUrl;
                if (!res.success || !signedUrl) {
                    this.toast.error(res.message ?? 'Failed to generate Work Cost Breakdown');
                    this.clearAllLoading(5);
                    return;
                }

                const analysisName =
                    this.dataService.analysisReport()?.displayName?.trim() || 'Geotechnical Analysis';
                const safeName = analysisName.replace(/[/\\?%*:|"<>]/g, '-');

                if (mode === 'preview') {
                    this.openXlsxInOnlyOffice(signedUrl, `${safeName} - Work Cost Breakdown`);
                } else {
                    try {
                        await this.downloadXlsxBlob(signedUrl, `${safeName} - Work Cost Breakdown.xlsx`);
                        this.toast.success('Work Cost Breakdown downloaded successfully');
                    } catch (err) {
                        const msg = err instanceof Error ? err.message : 'Failed to download file';
                        this.toast.error(msg + '. Please try again.');
                    }
                }

                this.clearAllLoading(5);
            },
            error: () => {
                this.toast.error('Failed to generate Work Cost Breakdown. Please try again.');
                this.clearAllLoading(5);
            },
        });
    }

    // ── Impact vs. Alternate (index 6) ───────────────────────────────────

    /**
     * Calls the backend to generate the Impact vs. Alternate XLSX.
     * - 'preview'  → opens in OnlyOffice (view-only)
     * - 'download' → fetches blob and triggers browser save-as
     */
    private handleImpactVsAlternate(mode: DownloadMode): void {
        const analysisId = this.dataService.analysisReportId();
        if (!analysisId) {
            this.toast.error('Analysis ID not found');
            this.clearAllLoading(6);
            return;
        }

        this.geoService.exportImpactVsAlternate(analysisId).subscribe({
            next: async (res) => {
                const signedUrl = res.data?.signedUrl;
                if (!res.success || !signedUrl) {
                    this.toast.error(res.message ?? 'Failed to generate Impact vs. Alternate');
                    this.clearAllLoading(6);
                    return;
                }

                const analysisName =
                    this.dataService.analysisReport()?.displayName?.trim() || 'Geotechnical Analysis';
                const safeName = analysisName.replace(/[/\\?%*:|"<>]/g, '-');

                if (mode === 'preview') {
                    this.openXlsxInOnlyOffice(signedUrl, `${safeName} - Impact vs. Alternate`);
                } else {
                    try {
                        await this.downloadXlsxBlob(signedUrl, `${safeName} - Impact vs. Alternate.xlsx`);
                        this.toast.success('Impact vs. Alternate downloaded successfully');
                    } catch (err) {
                        const msg = err instanceof Error ? err.message : 'Failed to download file';
                        this.toast.error(msg + '. Please try again.');
                    }
                }

                this.clearAllLoading(6);
            },
            error: () => {
                this.toast.error('Failed to generate Impact vs. Alternate. Please try again.');
                this.clearAllLoading(6);
            },
        });
    }

    // ── Shared XLSX download helper ──────────────────────────────────────

    /**
     * Fetches an XLSX file from the given signed S3 URL and triggers a browser save-as dialog.
     *
     * Uses `response.ok` guard so that S3 error responses (4xx/5xx) — which `fetch` resolves
     * rather than rejects — are surfaced as proper errors instead of silently downloading
     * a tiny XML error body.
     */
    private async downloadXlsxBlob(signedUrl: string, filename: string): Promise<void> {
        const response = await fetch(signedUrl);
        if (!response.ok) {
            throw new Error(`Failed to fetch file from storage (HTTP ${response.status})`);
        }
        const blob = await response.blob();
        if (blob.size === 0) {
            throw new Error('Downloaded file is empty');
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 2000);
    }

    /**
     * Builds an OnlyOffice IConfig for the given XLSX signed URL and opens the XLSX preview dialog.
     * Uses view-only mode — no editing or saving back to the server.
     */
    private openXlsxInOnlyOffice(signedUrl: string, title: string): void {
        const currentUser = this.authService.getUser();
        const tokenConfig: Partial<IConfig> = {
            document: {
                fileType: 'xlsx',
                // Unique key prevents OnlyOffice from serving a stale cached version
                key: `export-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                title,
                url: signedUrl,
                permissions: { download: true, edit: false, print: true },
            },
            documentType: ONLY_OFFICE_EDITOR_TYPE.EXCEL,
            editorConfig: {
                user: {
                    id: currentUser?.email,
                    name: [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ') || currentUser?.email,
                },
                mode: 'view',
            },
            height: '100%',
            width: '100%',
        };
        const token = this.jwtService.signToken(tokenConfig, environment.jwtSecret);
        this.xlsxEditorConfig = { ...tokenConfig, token };
        this.xlsxPreviewTitle = title;
        this.xlsxPreviewVisible = true;
    }

    // ── Status-aware toast helper ────────────────────────────────────────

    /**
     * Shows a contextual toast based on the current generation status of a
     * PDF summary (Report Summary or Analysis Summary).
     * Uses `info` for transient states (in-progress, not-started) and
     * `error` for terminal failures.
     */
    private showSummaryStatusToast(
        status: GEOTECHNICAL_SUMMARY_STATUS | undefined | null,
        label: string,
    ): void {
        switch (status) {
            case GEOTECHNICAL_SUMMARY_STATUS.IN_PROGRESS:
                this.toast.info(`${label} is still being generated. Please check back shortly.`);
                break;
            case GEOTECHNICAL_SUMMARY_STATUS.FAILED:
                this.toast.error(`${label} generation failed. Please retry from the ${label} tab.`);
                break;
            case GEOTECHNICAL_SUMMARY_STATUS.NOT_STARTED:
                this.toast.info(`${label} has not been generated yet. Please generate it first.`);
                break;
            default:
                // No status or unknown — summary was never created
                this.toast.info(`${label} is not available yet. Please generate it first.`);
        }
    }

    // ── Shared blob handler ──────────────────────────────────────────────

    private handleBlob(blob: Blob, filename: string, mode: DownloadMode, title: string): void {
        const url = URL.createObjectURL(blob);

        switch (mode) {
            case 'preview':
                // Revoke any previous URL to prevent memory leaks
                if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
                this.previewUrl = url;
                this.previewTitle = title;
                this.previewType = 'pdf';
                this.previewVisible = true;
                break;

            case 'download': {
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => URL.revokeObjectURL(url), 1000);
                this.toast.success(`${title} downloaded successfully`);
                break;
            }

            case 'open':
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 1000);
                break;
        }
    }

    onPreviewClose(): void {
        this.previewVisible = false;
        // Revoke PDF blob URL
        if (this.previewUrl) {
            URL.revokeObjectURL(this.previewUrl);
            this.previewUrl = '';
        }
        // Revoke HTML blob URL
        if (this._htmlBlobUrl) {
            URL.revokeObjectURL(this._htmlBlobUrl);
            this._htmlBlobUrl = null;
            this.previewHtmlSrc = null;
        }
    }

    private extractFilename(contentDisposition: string | null, fallback: string): string {
        if (!contentDisposition) return fallback;
        const match =
            contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i) ??
            contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
        return match?.[1] ? decodeURIComponent(match[1].trim()) : fallback;
    }

    private clearAllLoading(index: number): void {
        this.loadingMap.update(m => {
            const next = { ...m };
            (['preview', 'download', 'open'] as DownloadMode[]).forEach(mode => {
                delete next[`${index}:${mode}`];
            });
            return next;
        });
    }
}
